import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  insertPhoneNumberChangePhoneNumber,
  insertPhoneNumberChangeSecondaryPhoneNumber,
  verificationPhoneNumberChangePhoneNumber,
} from './change-phone-number-thunk';
import {
  ChangePhoneNumberState,
  PhoneNumberNotEditableType,
  StepsChangePhoneNumber,
} from './change-phone-number-type';

const initialState: ChangePhoneNumberState = {
  phoneNumber: '',
  confirmNewPhoneNumber: '',
  insertPhoneNumberChangePhoneNumberRequestStatus: 'idle',

  changePhoneNumberVerificationCodeEmail: '',
  phoneNumberAlreadyVerificated: false,
  phoneNumberAttempts: 0,
  verificationPhoneNumberChangePhoneNumberRequestStatus: 'idle',

  step: 'INSERIMENTO_DATI',

  openDialogChangePhoneNumber: false,
  changePhoneNumberVerificationCodePhoneNumber: '',
  emailAttempts: 0,
  countdown: '',
  insertPhoneNumberChangeSecondaryPhoneNumberRequestStatus: 'idle',
  openDialogChangeSecondaryPhoneNumber: false,
};

const changePhoneNumberSlice = createSlice({
  name: 'features/change-phone-number',
  initialState,
  reducers: {
    //reset state ChangePhoneNumber
    resetChangePhoneNumberState(state) {
      state.phoneNumber = '';
      state.confirmNewPhoneNumber = '';
      state.insertPhoneNumberChangePhoneNumberRequestStatus = 'idle';
      state.changePhoneNumberVerificationCodeEmail = '';
      state.changePhoneNumberVerificationCodePhoneNumber = '';
      state.phoneNumberAttempts = 0;
      state.verificationPhoneNumberChangePhoneNumberRequestStatus = 'idle';
      state.step = 'INSERIMENTO_DATI';
      state.openDialogChangePhoneNumber = false;
    },
    //reset state ChangeSecondaryPhoneNumber
    resetChangeSecondaryPhoneNumberState(state) {
      state.secondaryPhoneNumber = undefined;
      state.confirmNewSecondaryPhoneNumber = undefined;
      state.insertPhoneNumberChangeSecondaryPhoneNumberRequestStatus = 'idle';
      state.openDialogChangeSecondaryPhoneNumber = false;
    },
    //Insert Phone Number
    resetInsertPhoneNumberChangePhoneNumberRequestState(state) {
      state.insertPhoneNumberChangePhoneNumberError = undefined;
      state.insertPhoneNumberChangePhoneNumberRequestStatus = 'idle';
    },
    clearInsertPhoneNumberChangePhoneNumberError(state) {
      state.insertPhoneNumberChangePhoneNumberError = undefined;
      state.insertPhoneNumberChangePhoneNumberRequestStatus = 'idle';
    },
    setPhoneNumberChangePhoneNumber(state, action: PayloadAction<string>) {
      state.phoneNumber = action.payload;
    },
    setConfirmNewPhoneNumberChangePhoneNumber(
      state,
      action: PayloadAction<string>
    ) {
      state.confirmNewPhoneNumber = action.payload;
    },
    //Verification PhoneNumber
    resetVerificationPhoneNumberChangePhoneNumberRequestState(state) {
      state.verificationPhoneNumberChangePhoneNumberError = undefined;
      state.verificationPhoneNumberChangePhoneNumberRequestStatus = 'idle';
    },
    clearVerificationPhoneNumberChangePhoneNumberError(state) {
      state.verificationPhoneNumberChangePhoneNumberError = undefined;
      state.verificationPhoneNumberChangePhoneNumberRequestStatus = 'idle';
    },
    setVerificationPhoneNumberCodeChangePhoneNumberCode(
      state,
      action: PayloadAction<string>
    ) {
      state.changePhoneNumberVerificationCodePhoneNumber = action.payload;
    },
    //*** */
    setVerificationEmailCodeChangePhoneNumberCode(
      state,
      action: PayloadAction<string>
    ) {
      state.changePhoneNumberVerificationCodeEmail = action.payload;
    },
    //*** */
    clearVerificationPhoneNumberFields(state) {
      state.changePhoneNumberVerificationCodeEmail = '';
    },
    // Update step
    updateStepPhoneNumber(
      state,
      action: PayloadAction<StepsChangePhoneNumber>
    ) {
      state.step = action.payload;
    },
    updatePhoneNumberAlreadyVerificated(state, action: PayloadAction<boolean>) {
      state.phoneNumberAlreadyVerificated = action.payload;
    },
    // Open/Close Dialog
    updateOpenDialogChangePhoneNumber(state, action: PayloadAction<boolean>) {
      state.openDialogChangePhoneNumber = action.payload;
    },
    updateOpenDialogChangeSecondaryPhoneNumber(
      state,
      action: PayloadAction<boolean>
    ) {
      state.openDialogChangeSecondaryPhoneNumber = action.payload;
    },
    // Open error phone number not editable
    updateErrorPhoneNumberNotEditable(
      state,
      action: PayloadAction<PhoneNumberNotEditableType>
    ) {
      state.errorPhoneNumberNotEditable = action.payload;
    },
    updateCountdownPhoneNumber(state, action: PayloadAction<string>) {
      state.countdown = action.payload;
    },
    //Insert Second Phone Number
    resetInsertPhoneNumberChangeSecondaryPhoneNumberRequestState(state) {
      state.insertPhoneNumberChangeSecondaryPhoneNumberError = undefined;
      state.insertPhoneNumberChangeSecondaryPhoneNumberRequestStatus = 'idle';
    },
    clearInsertPhoneNumberChangeSecondaryPhoneNumberError(state) {
      state.insertPhoneNumberChangeSecondaryPhoneNumberError = undefined;
      state.insertPhoneNumberChangeSecondaryPhoneNumberRequestStatus = 'idle';
    },
    setPhoneNumberChangeSecondaryPhoneNumber(
      state,
      action: PayloadAction<string>
    ) {
      state.secondaryPhoneNumber = action.payload;
    },
    setConfirmNewPhoneNumberChangeSecondaryPhoneNumber(
      state,
      action: PayloadAction<string>
    ) {
      state.confirmNewSecondaryPhoneNumber = action.payload;
    },
  },
  extraReducers: (builder) => {
    //Insert PhoneNumber
    builder.addCase(insertPhoneNumberChangePhoneNumber.pending, (state) => {
      state.insertPhoneNumberChangePhoneNumberRequestStatus = 'loading';
    });
    builder.addCase(
      insertPhoneNumberChangePhoneNumber.fulfilled,
      (state, action) => {
        state.endtimeCountdown = action.payload.timeoutOTP;
        state.insertPhoneNumberChangePhoneNumberRequestStatus = 'succeeded';
      }
    );
    builder.addCase(
      insertPhoneNumberChangePhoneNumber.rejected,
      (state, action) => {
        state.insertPhoneNumberChangePhoneNumberError = action.payload;
        state.insertPhoneNumberChangePhoneNumberRequestStatus = 'failed';
      }
    );
    //Verification PhoneNumber
    builder.addCase(
      verificationPhoneNumberChangePhoneNumber.pending,
      (state) => {
        state.verificationPhoneNumberChangePhoneNumberRequestStatus = 'loading';
      }
    );
    builder.addCase(
      verificationPhoneNumberChangePhoneNumber.fulfilled,
      (state) => {
        state.verificationPhoneNumberChangePhoneNumberRequestStatus =
          'succeeded';
      }
    );
    builder.addCase(
      verificationPhoneNumberChangePhoneNumber.rejected,
      (state, action) => {
        state.verificationPhoneNumberChangePhoneNumberError = action.payload;
        if (
          action.payload.type === 'CODICE_DI_VERIFICA_SBAGLIATO' ||
          action.payload.type === 'CODICE_DI_VERIFICA_EMAIL_SBAGLIATO' ||
          action.payload.type === 'CODICE_DI_VERIFICA_CELLULARE_SBAGLIATO'
        ) {
          state.emailAttempts = action.payload.attemptsEmail;
          state.phoneNumberAttempts = action.payload.attemptsPhoneNumber;
        }
        state.verificationPhoneNumberChangePhoneNumberRequestStatus = 'failed';
      }
    );
    //Insert Secondary Phone Number
    builder.addCase(
      insertPhoneNumberChangeSecondaryPhoneNumber.pending,
      (state) => {
        state.insertPhoneNumberChangeSecondaryPhoneNumberRequestStatus =
          'loading';
      }
    );
    builder.addCase(
      insertPhoneNumberChangeSecondaryPhoneNumber.fulfilled,
      (state) => {
        state.insertPhoneNumberChangeSecondaryPhoneNumberRequestStatus =
          'succeeded';
      }
    );
    builder.addCase(
      insertPhoneNumberChangeSecondaryPhoneNumber.rejected,
      (state, action) => {
        state.insertPhoneNumberChangeSecondaryPhoneNumberError = action.payload;
        state.insertPhoneNumberChangeSecondaryPhoneNumberRequestStatus =
          'failed';
      }
    );
  },
});

export const {
  resetChangePhoneNumberState,
  resetInsertPhoneNumberChangePhoneNumberRequestState,
  clearInsertPhoneNumberChangePhoneNumberError,
  setPhoneNumberChangePhoneNumber,
  setConfirmNewPhoneNumberChangePhoneNumber,
  resetVerificationPhoneNumberChangePhoneNumberRequestState,
  clearVerificationPhoneNumberChangePhoneNumberError,
  setVerificationPhoneNumberCodeChangePhoneNumberCode,
  setVerificationEmailCodeChangePhoneNumberCode,
  updatePhoneNumberAlreadyVerificated,
  updateStepPhoneNumber,
  updateOpenDialogChangePhoneNumber,
  updateErrorPhoneNumberNotEditable,
  updateCountdownPhoneNumber,
  resetInsertPhoneNumberChangeSecondaryPhoneNumberRequestState,
  clearInsertPhoneNumberChangeSecondaryPhoneNumberError,
  setPhoneNumberChangeSecondaryPhoneNumber,
  setConfirmNewPhoneNumberChangeSecondaryPhoneNumber,
  updateOpenDialogChangeSecondaryPhoneNumber,
  resetChangeSecondaryPhoneNumberState,
} = changePhoneNumberSlice.actions;

export default changePhoneNumberSlice.reducer;
